// AdminPage.js - Admin panel for managing data sources
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./DataSourcesPage.css";

// API base URL
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Options for dropdowns
const REFRESH_FREQUENCIES = ["Weekly", "Monthly", "Quarterly", "Annual"];
const COLLECTION_STATUSES = ["On-time", "Delayed"];
const RECORD_STATUSES = ["Active", "Inactive"];

/**
 * Calculate collection status based on refresh frequency and last modified date.
 * If the time since last modification exceeds the refresh interval, status is "Delayed".
 * @param {string} refreshFrequency - One of: "Weekly", "Monthly", "Quarterly", "Annual"
 * @param {string} lastModifiedDate - ISO date string of last modification
 * @returns {string} - "On-time" or "Delayed"
 */
function calculateCollectionStatus(refreshFrequency, lastModifiedDate) {
    if (!lastModifiedDate || !refreshFrequency) {
        return "On-time"; // Default if data is missing
    }

    const lastModified = new Date(lastModifiedDate);
    const now = new Date();

    // Calculate the expected next refresh date based on frequency
    let expectedNextRefresh = new Date(lastModified);

    switch (refreshFrequency) {
        case "Weekly":
            expectedNextRefresh.setDate(expectedNextRefresh.getDate() + 7);
            break;
        case "Monthly":
            expectedNextRefresh.setMonth(expectedNextRefresh.getMonth() + 1);
            break;
        case "Quarterly":
            expectedNextRefresh.setMonth(expectedNextRefresh.getMonth() + 3);
            break;
        case "Annual":
            expectedNextRefresh.setFullYear(expectedNextRefresh.getFullYear() + 1);
            break;
        default:
            return "On-time"; // Unknown frequency, default to on-time
    }

    // If current date is past the expected next refresh date, status is Delayed
    return now > expectedNextRefresh ? "Delayed" : "On-time";
}

// Toast notification component
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="toast-container">
            <div className={`toast ${type}`}>
                {type === "success" ? "✓" : "⚠"} {message}
            </div>
        </div>
    );
}

// Confirmation modal
function ConfirmModal({ title, message, onConfirm, onCancel }) {
    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>{title}</h3>
                <p>{message}</p>
                <div className="modal-actions">
                    <button className="btn btn-outline" onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="btn btn-danger" onClick={onConfirm}>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

// Login form component
function LoginForm({ onLogin }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE}/api/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Login failed");
            }

            onLogin(data.token, data.username);
        } catch (err) {
            setError(err.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <h2>🔐 Admin Login</h2>
            <form className="login-form" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter username"
                        required
                        autoComplete="username"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        required
                        autoComplete="current-password"
                    />
                </div>
                {error && <p className="error-message">{error}</p>}
                <button type="submit" disabled={loading}>
                    {loading ? "Logging in..." : "Login"}
                </button>
            </form>
        </div>
    );
}

// Admin dashboard component
function AdminDashboard({ token, username, onLogout }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [showAddForm, setShowAddForm] = useState(false);
    const [newRecord, setNewRecord] = useState({
        data_record: "",
        sources: "",
        refresh_frequency: "Weekly",
        last_modified_date: new Date().toISOString().split("T")[0],
        data_collection_status: "On-time",
        record_status: "Active",
    });
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const showToast = useCallback((message, type = "success") => {
        setToast({ message, type });
    }, []);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/admin/datasources`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 401) {
                onLogout();
                return;
            }

            const data = await response.json();
            setRecords(data.records || []);
        } catch (err) {
            showToast("Failed to load records", "error");
        } finally {
            setLoading(false);
        }
    }, [token, onLogout, showToast]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        try {
            return new Date(dateStr).toISOString().split("T")[0];
        } catch {
            return "";
        }
    };

    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return "-";
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    const startEdit = (record) => {
        setEditingId(record.id);
        setEditForm({
            ...record,
            last_modified_date: formatDateForInput(record.last_modified_date),
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE}/api/admin/datasources/${editingId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(editForm),
            });

            if (response.status === 401) {
                onLogout();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Update failed");
            }

            showToast("Record updated successfully");
            setEditingId(null);
            setEditForm({});
            fetchRecords();
        } catch (err) {
            showToast(err.message || "Failed to update record", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        if (!newRecord.data_record || !newRecord.sources) {
            showToast("Data Record and Sources are required", "error");
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_BASE}/api/admin/datasources`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newRecord),
            });

            if (response.status === 401) {
                onLogout();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Create failed");
            }

            showToast("Record created successfully");
            setShowAddForm(false);
            setNewRecord({
                data_record: "",
                sources: "",
                refresh_frequency: "Weekly",
                last_modified_date: new Date().toISOString().split("T")[0],
                data_collection_status: "On-time",
                record_status: "Active",
            });
            fetchRecords();
        } catch (err) {
            showToast(err.message || "Failed to create record", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE}/api/admin/datasources/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 401) {
                onLogout();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Delete failed");
            }

            showToast("Record deleted successfully");
            setDeleteConfirm(null);
            fetchRecords();
        } catch (err) {
            showToast(err.message || "Failed to delete record", "error");
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadgeClass = (status, type) => {
        if (type === "collection") {
            return status === "On-time" ? "status-badge on-time" : "status-badge delayed";
        }
        return status === "Active" ? "status-badge active" : "status-badge inactive";
    };

    return (
        <div className="admin-container">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {deleteConfirm && (
                <ConfirmModal
                    title="Delete Record"
                    message="Are you sure you want to delete this record? This action cannot be undone."
                    onConfirm={() => handleDelete(deleteConfirm)}
                    onCancel={() => setDeleteConfirm(null)}
                />
            )}

            {/* Navigation */}
            <div className="datasources-nav">
                <Link to="/" className="back-link">
                    ← Back to Optibot
                </Link>
                <div>
                    <span style={{ marginRight: 16, color: "#6c757d" }}>
                        Logged in as: <strong>{username}</strong>
                    </span>
                    <button className="btn btn-outline" onClick={onLogout}>
                        Logout
                    </button>
                </div>
            </div>

            {/* Header */}
            <div className="datasources-header">
                <h1>Data Sources Admin</h1>
                <p className="helper-text">
                    Manage data source records. Changes are reflected immediately on the public view.
                </p>
            </div>

            {/* Toolbar */}
            <div className="admin-toolbar">
                <div className="toolbar-left">
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowAddForm(!showAddForm)}
                        disabled={saving}
                    >
                        {showAddForm ? "Cancel" : "+ Add New Record"}
                    </button>
                    <Link to="/datasources" className="btn btn-outline">
                        View Public Page
                    </Link>
                </div>
                <div className="toolbar-right">
                    <button className="btn btn-secondary" onClick={fetchRecords} disabled={loading}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>
            </div>

            {/* Add New Record Form */}
            {showAddForm && (
                <div className="datasources-card" style={{ marginBottom: 16, padding: 20 }}>
                    <h3 style={{ marginBottom: 16 }}>Add New Record</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Data Record *</label>
                            <input
                                type="text"
                                value={newRecord.data_record}
                                onChange={(e) => setNewRecord({ ...newRecord, data_record: e.target.value })}
                                placeholder="Enter data record name"
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Sources *</label>
                            <input
                                type="text"
                                value={newRecord.sources}
                                onChange={(e) => setNewRecord({ ...newRecord, sources: e.target.value })}
                                placeholder="Enter source name"
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Refresh Frequency</label>
                            <select
                                value={newRecord.refresh_frequency}
                                onChange={(e) => setNewRecord({ ...newRecord, refresh_frequency: e.target.value })}
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            >
                                {REFRESH_FREQUENCIES.map((f) => (
                                    <option key={f} value={f}>{f}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Last Modified Date</label>
                            <input
                                type="date"
                                value={newRecord.last_modified_date}
                                onChange={(e) => setNewRecord({ ...newRecord, last_modified_date: e.target.value })}
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Collection Status</label>
                            <select
                                value={newRecord.data_collection_status}
                                onChange={(e) => setNewRecord({ ...newRecord, data_collection_status: e.target.value })}
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            >
                                {COLLECTION_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Record Status</label>
                            <select
                                value={newRecord.record_status}
                                onChange={(e) => setNewRecord({ ...newRecord, record_status: e.target.value })}
                                style={{ width: "100%", padding: "10px", border: "1px solid #dee2e6", borderRadius: 4 }}
                            >
                                {RECORD_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
                            {saving ? "Saving..." : "Save Record"}
                        </button>
                    </div>
                </div>
            )}

            {/* Data Table */}
            <div className="datasources-card">
                {loading ? (
                    <div className="datasources-loading">
                        <div className="spinner"></div>
                        <p>Loading records...</p>
                    </div>
                ) : records.length === 0 ? (
                    <div className="datasources-empty">
                        <p>No records found. Click "Add New Record" to create one.</p>
                    </div>
                ) : (
                    <table className="datasources-table editable-table">
                        <thead>
                            <tr>
                                <th>Data Record</th>
                                <th>Sources</th>
                                <th>Refresh Frequency</th>
                                <th>Last Modified Date</th>
                                <th>Collection Status</th>
                                <th>Record Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record) => (
                                <tr key={record.id}>
                                    {editingId === record.id ? (
                                        <>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={editForm.data_record || ""}
                                                    onChange={(e) => setEditForm({ ...editForm, data_record: e.target.value })}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={editForm.sources || ""}
                                                    onChange={(e) => setEditForm({ ...editForm, sources: e.target.value })}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    value={editForm.refresh_frequency || "Weekly"}
                                                    onChange={(e) => setEditForm({ ...editForm, refresh_frequency: e.target.value })}
                                                >
                                                    {REFRESH_FREQUENCIES.map((f) => (
                                                        <option key={f} value={f}>{f}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    type="date"
                                                    value={editForm.last_modified_date || ""}
                                                    onChange={(e) => setEditForm({ ...editForm, last_modified_date: e.target.value })}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    value={editForm.data_collection_status || "On-time"}
                                                    onChange={(e) => setEditForm({ ...editForm, data_collection_status: e.target.value })}
                                                >
                                                    {COLLECTION_STATUSES.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    value={editForm.record_status || "Active"}
                                                    onChange={(e) => setEditForm({ ...editForm, record_status: e.target.value })}
                                                >
                                                    {RECORD_STATUSES.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="btn btn-primary action-btn"
                                                    onClick={saveEdit}
                                                    disabled={saving}
                                                >
                                                    {saving ? "..." : "Save"}
                                                </button>
                                                <button
                                                    className="btn btn-outline action-btn"
                                                    onClick={cancelEdit}
                                                    disabled={saving}
                                                >
                                                    Cancel
                                                </button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{record.data_record}</td>
                                            <td>{record.sources}</td>
                                            <td>{record.refresh_frequency}</td>
                                            <td>{formatDateDisplay(record.last_modified_date)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(calculateCollectionStatus(record.refresh_frequency, record.last_modified_date), "collection")}>
                                                    {calculateCollectionStatus(record.refresh_frequency, record.last_modified_date)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={getStatusBadgeClass(record.record_status, "record")}>
                                                    {record.record_status}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="btn btn-outline action-btn"
                                                    onClick={() => startEdit(record)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger action-btn"
                                                    onClick={() => setDeleteConfirm(record.id)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// Main Admin Page component
function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem("adminToken"));
    const [username, setUsername] = useState(() => sessionStorage.getItem("adminUser"));

    const handleLogin = (newToken, user) => {
        sessionStorage.setItem("adminToken", newToken);
        sessionStorage.setItem("adminUser", user);
        setToken(newToken);
        setUsername(user);
    };

    const handleLogout = () => {
        sessionStorage.removeItem("adminToken");
        sessionStorage.removeItem("adminUser");
        setToken(null);
        setUsername(null);
    };

    if (!token) {
        return <LoginForm onLogin={handleLogin} />;
    }

    return (
        <AdminDashboard
            token={token}
            username={username}
            onLogout={handleLogout}
        />
    );
}

export default AdminPage;
